/**
 * The 90-minute discharge timer control: start button, countdown, or complete.
 *
 * Rendered in two places against the same store state — on the board-list tile,
 * where a tech starts several buildings and watches them run down together, and
 * inside the group drill-down. Starting from either must be indistinguishable, so
 * both use this one component.
 *
 * Props:
 *   groupCode      — group name, used in the accessible labels
 *   status         — 'idle' | 'running' | 'complete'
 *   remainingMs    — countdown when running (displayed as mm:ss)
 *   onStart        — start the timer (idle only)
 *   onRequestReset — reset request (running / complete)
 *   className      — extra class(es) on the row
 */
function formatMmSs(ms) {
  if (ms == null || ms <= 0) return '00:00';
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export default function DischargeTimerRow({
  groupCode,
  status = 'idle',
  remainingMs = 0,
  onStart,
  onRequestReset,
  className = '',
}) {
  const timerState = status === 'running' || status === 'complete' ? status : 'idle';

  return (
    <div className={['mobile-asset-group-emerg__timer', className].filter(Boolean).join(' ')}>
      {timerState === 'idle' && (
        <>
          <button
            type="button"
            className="mobile-asset-group-emerg__action"
            onClick={onStart}
            aria-label={`Start 90 minute timer for ${groupCode}`}
          >
            Start 90 min timer
          </button>
          <span className="mobile-asset-group-emerg__status">Not started</span>
        </>
      )}

      {timerState === 'running' && (
        <>
          <button
            type="button"
            className="mobile-asset-group-emerg__action mobile-asset-group-emerg__action--running"
            onClick={onRequestReset}
            aria-label={`Timer remaining ${formatMmSs(remainingMs)}`}
          >
            {formatMmSs(remainingMs)}
          </button>
          <span className="mobile-asset-group-emerg__status">Timer running</span>
        </>
      )}

      {timerState === 'complete' && (
        <>
          <button
            type="button"
            className="mobile-asset-group-emerg__action mobile-asset-group-emerg__action--complete"
            onClick={onRequestReset}
            aria-label="90 minute timer complete"
          >
            00:00
          </button>
          <span className="mobile-asset-group-emerg__status">Complete</span>
        </>
      )}
    </div>
  );
}
